import { useState } from 'react';
import { api, useMutation } from '../api';
import { useT, type Dict } from '../i18n';
import { ErrorState } from './feedback';

/**
 * Self-service password change (POST /auth/change-password): verifies the current
 * password, enforces the shared password policy, and revokes the user's other sessions
 * server-side. Shared by the agent and admin Settings screens.
 */
const dict: Dict = {
  en: {
    passwordHeading: 'Password',
    currentPasswordLabel: 'Current password',
    newPasswordLabel: 'New password',
    passwordHint: 'At least 12 characters, with an uppercase letter, a lowercase letter and a digit.',
    confirmPasswordLabel: 'Confirm new password',
    passwordMismatch: 'Passwords do not match.',
    passwordMismatchValidation: 'New password and confirmation do not match.',
    passwordChanged: 'Password changed — you may need to sign in again.',
    passwordSessionsNotice: 'Changing your password signs out your other sessions.',
    updatingBtn: 'Updating…',
    changePasswordBtn: 'Change password',
  },
  es: {
    passwordHeading: 'Contraseña',
    currentPasswordLabel: 'Contraseña actual',
    newPasswordLabel: 'Nueva contraseña',
    passwordHint: 'Al menos 12 caracteres, con una letra mayúscula, una minúscula y un número.',
    confirmPasswordLabel: 'Confirmar nueva contraseña',
    passwordMismatch: 'Las contraseñas no coinciden.',
    passwordMismatchValidation: 'La nueva contraseña y la confirmación no coinciden.',
    passwordChanged: 'Contraseña cambiada — puede que necesites iniciar sesión de nuevo.',
    passwordSessionsNotice: 'Cambiar tu contraseña cierra el resto de tus sesiones.',
    updatingBtn: 'Actualizando…',
    changePasswordBtn: 'Cambiar contraseña',
  },
  de: {
    passwordHeading: 'Passwort',
    currentPasswordLabel: 'Aktuelles Passwort',
    newPasswordLabel: 'Neues Passwort',
    passwordHint: 'Mindestens 12 Zeichen, mit einem Großbuchstaben, einem Kleinbuchstaben und einer Ziffer.',
    confirmPasswordLabel: 'Neues Passwort bestätigen',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordMismatchValidation: 'Neues Passwort und Bestätigung stimmen nicht überein.',
    passwordChanged: 'Passwort geändert — möglicherweise müssen Sie sich erneut anmelden.',
    passwordSessionsNotice: 'Das Ändern Ihres Passworts meldet Ihre anderen Sitzungen ab.',
    updatingBtn: 'Aktualisiert…',
    changePasswordBtn: 'Passwort ändern',
  },
};

/** Mirrors the shared passwordSchema (>=12 chars, upper + lower + digit). */
function passwordOk(pw: string): boolean {
  return pw.length >= 12 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /[0-9]/.test(pw);
}

export function PasswordCard() {
  const t = useT(dict);
  const change = useMutation(api.auth.changePassword);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const weak = newPassword.length > 0 && !passwordOk(newPassword);
  const canSubmit =
    currentPassword.length > 0 && passwordOk(newPassword) && newPassword === confirm && !change.loading;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setDone(false);
    setLocalError(null);
    if (!passwordOk(newPassword)) {
      setLocalError(t('passwordHint'));
      return;
    }
    if (newPassword !== confirm) {
      setLocalError(t('passwordMismatchValidation'));
      return;
    }
    try {
      await change.mutate({ currentPassword, newPassword });
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch {
      /* change.error surfaced below */
    }
  }

  return (
    <form className="panel" style={{ overflow: 'hidden' }} onSubmit={submit}>
      <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h3">{t('passwordHeading')}</div>
      </div>
      <div className="col gap-12" style={{ padding: 16 }}>
        <label className="col gap-4">
          <span className="label">{t('currentPasswordLabel')}</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              setDone(false);
            }}
          />
        </label>
        <label className="col gap-4">
          <span className="label">{t('newPasswordLabel')}</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setDone(false);
            }}
          />
          <span className="meta" style={{ fontSize: 12, color: weak ? 'var(--red)' : undefined }}>
            {t('passwordHint')}
          </span>
        </label>
        <label className="col gap-4">
          <span className="label">{t('confirmPasswordLabel')}</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setDone(false);
            }}
          />
          {mismatch && (
            <span className="meta" style={{ fontSize: 12, color: 'var(--red)' }}>
              {t('passwordMismatch')}
            </span>
          )}
        </label>

        {localError && <ErrorState message={localError} />}
        {change.error && <ErrorState message={change.error.message} />}
        {done && (
          <div className="meta" style={{ color: 'var(--green)', fontSize: 13 }}>
            {t('passwordChanged')}
          </div>
        )}

        <div className="row between center">
          <span className="meta" style={{ fontSize: 12 }}>
            {t('passwordSessionsNotice')}
          </span>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            {change.loading ? t('updatingBtn') : t('changePasswordBtn')}
          </button>
        </div>
      </div>
    </form>
  );
}
